"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Menu, X } from "lucide-react";
import type { HeaderData, LanguageOption } from "../../types";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { GuardedLink, useDevPlaceholder } from "../../runtime/DevPlaceholder";
import { t } from "../../i18n/strings";
import { resolveLinkHref, resolveConfiguredHref } from "../../utils/links";
import { SiteHeaderMobile } from "./SiteHeaderMobile";
import { HeaderSearchForm } from "./HeaderSearchForm";
import { LanguageSelector } from "./LanguageSelector";
import { SocialBrandIcon } from "./footer/SocialBrandIcon";

/** Site header component props. */
export interface SiteHeaderProps {
  data: HeaderData;
  logoSrc: string;
  logoAlt?: string;
  homeHref?: string;
  language: string;
  languageOptions: LanguageOption[];
  defaultLanguageCode?: string;
  onLanguageChange?: (code: string) => void;
}

/** Site header with desktop/mobile navigation, search and language selector. */
export function SiteHeader({
  data,
  logoSrc,
  logoAlt = "APA Parts",
  homeHref = "/",
  language,
  languageOptions,
  defaultLanguageCode,
  onLanguageChange,
}: SiteHeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState<number | null>(null);
  const [menuLeft, setMenuLeft] = useState(0);
  const [pointerLeft, setPointerLeft] = useState(24);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);

  const { previewMode, notify } = useDevPlaceholder();
  const locale = useLocale();

  // In production, drop unconfigured (empty-href) links entirely; in preview, keep them so the
  // dev modal can explain they're not set up. Rules: a dropdown SECTION (an item defined with
  // children) is hidden entirely when none of its children resolve — even if the parent has its
  // own href — because an empty menu is worse than no menu (e.g. "Statistiques" with all-empty
  // children). A plain link (no children) survives only on its own configured href.
  const navItems = useMemo(() => {
    if (previewMode) return data.nav ?? [];
    return (data.nav ?? [])
      .filter((item) => {
        const rawChildren = item.children ?? [];
        if (rawChildren.length > 0) return rawChildren.some((child) => resolveConfiguredHref(child.href));
        return Boolean(resolveConfiguredHref(item.href));
      })
      .map((item) => ({ ...item, children: item.children?.filter((child) => resolveConfiguredHref(child.href)) }));
  }, [data.nav, previewMode]);
  const socials = previewMode ? data.socials : (data.socials ?? []).filter((social) => resolveConfiguredHref(social.href));

  useBodyScrollLock(mobileOpen);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 768px)");
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) setMobileOpen(false);
    };
    mql.addEventListener("change", handleChange);
    return () => {
      mql.removeEventListener("change", handleChange);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenDesktopMenu(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const openDesktopDropdown = (index: number, el: HTMLElement) => {
    setOpenDesktopMenu(index);
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const triggerRect = el.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const panelWidth = Math.min(420, viewportWidth - 32);
    const desiredLeft = triggerRect.left - containerRect.left - 40;
    const minLeft = 8;
    const maxLeft = containerRect.width - panelWidth - 8;
    const clampedLeft = Math.max(minLeft, Math.min(desiredLeft, maxLeft));
    setMenuLeft(clampedLeft);
    const pointerPos = triggerRect.left - containerRect.left - clampedLeft + 12;
    setPointerLeft(Math.max(16, Math.min(panelWidth - 24, pointerPos)));
  };

  useEffect(() => {
    const closeOnScrollResize = () => setOpenDesktopMenu(null);
    window.addEventListener("scroll", closeOnScrollResize, true);
    window.addEventListener("resize", closeOnScrollResize);
    return () => {
      window.removeEventListener("scroll", closeOnScrollResize, true);
      window.removeEventListener("resize", closeOnScrollResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 980px)");
    const handleSearchToggle = (event?: MediaQueryListEvent | MediaQueryList) => {
      setShowDesktopSearch(event ? event.matches : mql.matches);
    };
    handleSearchToggle(mql);
    mql.addEventListener("change", handleSearchToggle);
    return () => {
      mql.removeEventListener("change", handleSearchToggle);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const defaultLangCode = defaultLanguageCode ?? languageOptions[0]?.code ?? language;
  const currentLanguage =
    languageOptions.find((opt) => opt.code === language) ??
    languageOptions.find((opt) => opt.code === defaultLangCode) ??
    languageOptions[0];

  const handleLanguageChange = (code: string) => {
    onLanguageChange?.(code);
  };

  return (
    <header className={`fixed inset-x-0 top-0 z-30 bg-white ${isScrolled ? "shadow-[0_4px_10px_rgba(0,0,0,0.25)]" : "shadow-none"} transition-shadow duration-200`}>
      <div className="bg-white/90 border-b border-[#00B8622e]">
        <div className="apa-header-row relative mx-auto max-w-7xl px-6 md:px-8 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 md:gap-2 min-[900px]:gap-4 py-2 sm:py-3 text-sm xl:text-base min-h-[70px] xl:min-h-[78px]" onMouseEnter={() => setOpenDesktopMenu(null)}>
          <div className="flex items-center gap-3 sm:gap-4 md:gap-2 min-[900px]:gap-5 lg:gap-6 min-w-0">
            <RuntimeLink
              href={homeHref}
              className="flex items-center gap-2 font-semibold min-h-0 min-w-0 pr-2 md:pr-1 min-[900px]:pr-2"
              aria-label={logoAlt}
              style={{
                flex: "1 1 340px",
                maxWidth: "340px",
                minWidth: 0,
                minHeight: 0,
                height: "clamp(40px, 8vw, 44px)",
              }}
            >
              <RuntimeImage
                src={logoSrc}
                alt={logoAlt}
                className="h-full w-auto max-h-[44px] max-w-full min-w-0 min-h-0 object-contain transition-all duration-200"
                style={{ maxWidth: "340px" }}
              />
              <span className="sr-only">APA</span>
            </RuntimeLink>
            <nav
              className="hidden md:flex flex-nowrap items-center gap-6 ml-auto px-[22px] md:px-2 min-[900px]:px-[22px] whitespace-nowrap shrink-0"
              style={{ fontFamily: "var(--font-nunito)" }}
            >
              {(data.quickLinks ?? []).map((link) => (
                <GuardedLink
                  key={link.label}
                  label={link.label}
                  className="text-base xl:text-lg font-semibold text-[#00B862] hover:text-[#00B862]"
                  href={link.href}
                >
                  {link.label}
                </GuardedLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 md:gap-6 justify-self-end">
            <HeaderSearchForm showForm={showDesktopSearch} />
            <LanguageSelector
              variant="desktop"
              language={language}
              languageOptions={languageOptions}
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
            />
            {/* Soft tint + 15% blue border — secondary-button weight, matching the mobile
                menu's other pills. The previous solid-blue border read as a primary action. */}
            <button className="inline-flex items-center rounded-md border border-[#00B862]/15 bg-[#EEF1F0] px-3 py-2 md:hidden text-[#00B862] shrink-0 transition-colors hover:bg-[#EEF1F0]" aria-expanded={mobileOpen} aria-controls="mobile-menu" onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              <span className="sr-only">{t(locale, "openCloseMenu")}</span>
            </button>
          </div>
        </div>
      </div>
      {/* md+: bottom border on the nav row provides the separator between header and the
          below-header slot. Mobile keeps no border here so the (empty) wrapper stays invisible. */}
      <div className="bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 md:border-b md:border-[#00B8622e]">
        <div ref={containerRef} className="apa-header-row relative mx-auto max-w-7xl px-6 md:px-8 ">
          <nav className="relative hidden items-center gap-x-6 gap-y-3 text-sm xl:text-base md:flex py-[22px]">
            {/* Main nav items first; socials are appended after via ml-auto wrapper. */}
            {navItems.map((item, i) => {
              const itemHref = resolveConfiguredHref(item.href);
              const hasChildren = (item.children?.length ?? 0) > 0;
              return (
                <div key={item.label} className="relative" onMouseLeave={(e) => {
                  const related = (e as unknown as React.MouseEvent<HTMLDivElement> & { relatedTarget: Node | null }).relatedTarget as Node | null;
                  const container = containerRef.current;
                  if (container && related && container.contains(related)) return;
                  setOpenDesktopMenu(null);
                }}>
                  {itemHref ? (
                    <RuntimeLink
                      href={itemHref}
                      className={`text-base xl:text-lg max-[850px]:text-sm flex items-center font-semibold gap-1 whitespace-nowrap text-[#00B862] hover:text-[#00B862] ${openDesktopMenu === i ? "text-[#00B862]" : ""}`}
                      aria-haspopup={hasChildren ? "menu" : undefined}
                      aria-expanded={hasChildren ? openDesktopMenu === i : undefined}
                      onMouseEnter={hasChildren ? (e) => openDesktopDropdown(i, e.currentTarget) : undefined}
                      onClick={() => setOpenDesktopMenu(null)}
                    >
                      {item.label}
                      {hasChildren && <ChevronDown className={`h-4 w-4 text-[#00B862] transition-transform ${openDesktopMenu === i ? "rotate-180" : ""}`} />}
                    </RuntimeLink>
                  ) : hasChildren ? (
                    <button
                      type="button"
                      className={`text-base xl:text-lg max-[850px]:text-sm flex items-center font-semibold gap-1 whitespace-nowrap text-[#00B862] hover:text-[#00B862] ${openDesktopMenu === i ? "text-[#00B862]" : ""}`}
                      aria-haspopup="menu"
                      aria-expanded={openDesktopMenu === i}
                      onClick={(e) => openDesktopDropdown(i, e.currentTarget)}
                      onMouseEnter={(e) => openDesktopDropdown(i, e.currentTarget)}
                    >
                      {item.label} <ChevronDown className={`h-4 w-4 text-[#00B862] transition-transform ${openDesktopMenu === i ? "rotate-180" : ""}`} />
                    </button>
                  ) : (
                    // Empty leaf (no href, no children) — preview only (filtered out in production):
                    // clicking opens the "not configured yet" dev modal instead of going to the home page.
                    <button
                      type="button"
                      data-dev-placeholder="true"
                      className="text-base xl:text-lg max-[850px]:text-sm flex items-center font-semibold gap-1 whitespace-nowrap text-[#00B862] hover:text-[#00B862] cursor-pointer"
                      onClick={() => notify(item.label)}
                    >
                      {item.label}
                    </button>
                  )}
                </div>
              );
            })}
            {/* Social shortcuts pulled from siteSettings.header.socials. Yellow circular pills,
                CMS-driven so editors control which networks are exposed and in what order.
                Hidden below 1000px so the nav row stays clean on narrower viewports. */}
            {socials && socials.length > 0 && (
              <div className="ml-auto hidden min-[980px]:flex items-center gap-2">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={resolveLinkHref(social.href) ?? "#"}
                    aria-label={social.label}
                    target={social.icon === "mail" ? undefined : "_blank"}
                    rel={social.icon === "mail" ? undefined : "noreferrer"}
                    className="apa-header-social grid h-7 w-7 place-items-center rounded-full bg-[#FFC105] text-white transition-opacity hover:opacity-90"
                  >
                    <SocialBrandIcon type={social.icon ?? ""} />
                  </a>
                ))}
              </div>
            )}
          </nav>
          {openDesktopMenu !== null && navItems[openDesktopMenu] && (navItems[openDesktopMenu].children?.length ?? 0) > 0 && (
            <div className="absolute top-full z-50 mt-0 w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-md bg-white shadow-xl ring-1 ring-black/10" style={{ left: menuLeft }} onMouseEnter={() => { }} onMouseLeave={() => setOpenDesktopMenu(null)}>
              <span className="pointer-events-none absolute -top-2 block h-4 w-4 rotate-45 rounded-sm bg-white ring-1 ring-black/10" style={{ left: pointerLeft }} />
              <div className="md:flex">
                <ul className="space-y-1 px-3 py-4 md:flex-1">
                  {navItems[openDesktopMenu].children?.map((child) => {
                    return (
                      <li key={child.label}>
                        <GuardedLink label={child.label} className="group relative flex w-full max-w-full items-center justify-between rounded-md px-5 py-3.5 text-left transition-colors hover:bg-[#00935A]/8" href={child.href} onClick={() => setOpenDesktopMenu(null)}>
                          <span
                            className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-[16px] xl:text-[18px] font-bold leading-normal text-[#00B862]"
                            style={{ fontFamily: 'var(--font-nunito, "Nunito Sans"), "Nunito Sans", system-ui, -apple-system, "Segoe UI", sans-serif', fontStyle: "normal" }}
                          >
                            {child.label}
                          </span>
                          <ChevronRight className="h-4 w-4 shrink-0 text-[#FFC105]" aria-hidden="true" />
                        </GuardedLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {mobileOpen && (
        <SiteHeaderMobile
          data={data}
          logoSrc={logoSrc}
          logoAlt={logoAlt}
          homeHref={homeHref}
          language={language}
          languageOptions={languageOptions}
          currentLanguage={currentLanguage}
          onLanguageChange={handleLanguageChange}
          onClose={() => setMobileOpen(false)}
        />
      )}
    </header>
  );
}
